export const TASKS_STORAGE_KEY = 'tasks'
export const TASK_DETAILS_LINK = 'https://my.1tool.com/tasks_c/showTask'
export const TASK_BOARD_STORAGE_KEY = 'tasks_board'
export const PREVIEW_TASK_BG_COLOR = '#e5e7eb'
export const DONE_TASK_BG_COLOR = '#ecffe1'
export const DEFAULT_TASKS_BG_COLOR = '#ffffff'
export const TASK_MENU_ICON_ID = 'task-menu-icon'
export const TASKS_ROUTE_PARAM_FIELD = 'tabOrId'

export enum TASK_SORT_OPTION {
  MANUAL = 'manual',
  NAME = 'name',
  START_DATE = 'start_date',
  DEADLINE = 'deadline'
}

export function getTaskSortOptions(t: ReturnType<typeof useL10n>['t']) {
  return [
    {
      label: t('pages.tasks.manualSort'),
      value: TASK_SORT_OPTION.MANUAL
    },
    {
      label: t('pages.tasks.startDate'),
      value: TASK_SORT_OPTION.START_DATE
    },
    {
      label: t('pages.tasks.deadline'),
      value: TASK_SORT_OPTION.DEADLINE
    },
    {
      label: t('pages.tasks.title'),
      value: TASK_SORT_OPTION.NAME
    }
  ]
}

export enum TASK_GROUP_OPTION {
  TO_ME = 'USER',
  BY_ME = 'CREATED_BY',
  ALL_TASKS = 'ALL_TASKS'
}

export function getTaskGroupByOptions(t: ReturnType<typeof useL10n>['t']) {
  return [
    {
      label: t('pages.tasks.toMe'),
      value: TASK_GROUP_OPTION.TO_ME
    },
    {
      label: t('pages.tasks.byMe'),
      value: TASK_GROUP_OPTION.BY_ME
    },
    {
      label: t('pages.tasks.allTasks'),
      value: TASK_GROUP_OPTION.ALL_TASKS
    }
  ]
}

export enum VIEW_TYPE {
  GROUPED = 'GROUPED',
  UNGROUPED = 'UNGROUPED'
}

export function getTaskViewType(t: ReturnType<typeof useL10n>['t']) {
  return [
    {
      label: t('pages.tasks.ungroup'),
      value: VIEW_TYPE.UNGROUPED
    },
    {
      label: t('pages.tasks.grouped'),
      value: VIEW_TYPE.GROUPED
    }
  ]
}

export enum TASK_FILTER {
  FROM_USER = 'FROM_USER',
  TO = 'TO',
  PROJECT = 'PROJECT',
  GROUP = 'GROUP',
  VIEW_TYPE = 'VIEW_TYPE',
  SORT = 'SORT',
  SEARCH = 'SEARCH',
  DONE = 'DONE'
}

export function getTaskFilterOptions(
  t: ReturnType<typeof useL10n>['t'],
  hiddenFilters?: Set<TASK_FILTER>
) {
  return [
    {
      label: t('global.action.search'),
      value: TASK_FILTER.SEARCH
    },
    {
      label: t('pages.tickets.fromUser'),
      value: TASK_FILTER.FROM_USER
    },
    {
      label: t('pages.tasks.grouped'),
      value: TASK_FILTER.GROUP
    },
    {
      label: t('pages.tasks.viewType'),
      value: TASK_FILTER.VIEW_TYPE
    },
    {
      label: t('pages.tickets.to'),
      value: TASK_FILTER.TO
    },
    { label: t('pages.tickets.done'), value: TASK_FILTER.DONE },
    {
      label: t('pages.tickets.project'),
      value: TASK_FILTER.PROJECT
    },
    {
      label: t('pages.tasks.sort'),
      value: TASK_FILTER.SORT
    }
  ].filter((filter) => !hiddenFilters?.has(filter.value))
}

export enum TASK_BOARD_FILTER {
  USER = 'USER'
}

export function getTaskBoardFilterOptions(t: ReturnType<typeof useL10n>['t']) {
  return [
    {
      label: t('pages.boards.user'),
      value: TASK_BOARD_FILTER.USER
    }
  ]
}

export const startTaskTimeTracking = async (task: Task | BoardTask) => {
  const userStore = useUserStore()
  const body = {
    user_id: userStore.user.id,
    time_in: new Date(),
    project_id: (task as BoardTask).project?.id ?? (task as Task).project_relation?.id ?? null,
    task_id: task.id,
    contact_id: task.customer?.id || null,
    opportunity_id: task.opportunity ?? null
  }

  useTimetrackingStore().openTimetracking = (await useApi('time-tracking', 'POST', body)).data
}

export enum TASKS_TAB {
  OPEN = 'OPEN',
  TODAY = 'TODAY',
  LATER = 'LATER',
  PLANNED = 'PLANNED',
  ALL = 'ALL',
  ARCHIVED = 'ARCHIVED',
  BOARD = 'BOARD'
}

export const tasksTabs = [
  {
    id: TASKS_TAB.OPEN,
    route: '/tasks/open',
    labelKey: 'pages.tasks.tabs.open'
  },
  {
    id: TASKS_TAB.TODAY,
    route: '/tasks/today',
    labelKey: 'pages.tasks.tabs.today'
  },
  {
    id: TASKS_TAB.LATER,
    route: '/tasks/later',
    labelKey: 'pages.tasks.tabs.next'
  },
  {
    id: TASKS_TAB.PLANNED,
    route: '/tasks/planned',
    labelKey: 'pages.tasks.tabs.planned'
  },
  {
    id: TASKS_TAB.ALL,
    route: '/tasks/all',
    labelKey: 'pages.tasks.tabs.all'
  },
  {
    id: TASKS_TAB.ARCHIVED,
    route: '/tasks/archived',
    labelKey: 'pages.tasks.tabs.archive'
  },
  {
    id: TASKS_TAB.BOARD,
    route: '/tasks/board',
    labelKey: 'pages.tasks.tabs.board'
  }
]

export const tasksTabById = Object.fromEntries(tasksTabs.map((tab) => [tab.id, tab]))

export function getTaskMoveLabelKey(isTodayTab: boolean) {
  return isTodayTab ? 'pages.tasks.moveToNext' : 'pages.tasks.moveToToday'
}

export function isTodayTabActive(activeTabId: TASKS_TAB): boolean {
  return activeTabId === TASKS_TAB.TODAY
}

export function getTaskMoveConfirmationOptions(
  selectedTasksIds: Array<number>,
  api: ReturnType<typeof useExtendedApi>,
  t: ReturnType<typeof useL10n>['t'],
  isTodayTab: boolean,
  taskMoveLabelKey: string,
  updateSelectedTasks: (ids: Set<number>) => void,
  title?: string
) {
  return {
    message: t(
      isTodayTab ? 'pages.tasks.sureToMoveNext' : 'pages.tasks.sureToMoveToday',
      title ? { title } : {}
    ),
    acceptLabel: t('global.confirmation.yes'),
    header: t(taskMoveLabelKey),
    rejectLabel: t('global.confirmation.no'),
    accept: async () => {
      const savingStore = useSavingStore()
      const savingNumber = savingStore.setSaving()

      await Promise.all(
        selectedTasksIds.map((taskId) =>
          useApi(`tasks/${taskId}`, 'PUT', {
            custom_tab_mode: isTodayTab ? 'next' : null
          })
        )
      )

      savingStore.setSaved(savingNumber)
      updateSelectedTasks(new Set())
      api.refresh()
    }
  }
}

export const getTaskSortKey = (tabId: TASKS_TAB) => {
  if (tabId === TASKS_TAB.TODAY) return 'today_pos'
  if (tabId === TASKS_TAB.LATER) return 'next_pos'
  return 'someday_pos'
}

export function getTaskDetailsUrl(taskId?: number) {
  return `${useTenantSettingsStore().taskDetailsBaseUrl}/${taskId}`
}
